import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { FormControl, LoadingButton, PageTitle, PageWrapper } from '@ingrid/luterkort-react';

import { MutationBanner } from '~src/components/MutationNotification/MutationNotification';
import { useTranslation } from '~src/i18n/use-translation';
import { useToast } from '~src/main/hooks/useToast';

import { AddressBookForm, FormValues } from '../components/address-book-form';
import { Breadcrumbs } from '../components/breadcrumbs';
import { useAddressBookCreateMutation } from '../mutations';

interface Props {
  defaultValues?: FormValues;
}

export function Page({ defaultValues }: Props) {
  const form = useForm<FormValues>({
    defaultValues: defaultValues ?? {
      address: {
        address_lines: [''],
      },
    },
  });
  const mutation = useAddressBookCreateMutation();

  const navigate = useNavigate();

  const { t } = useTranslation();
  const { toast } = useToast();

  async function onSubmit(payload: FormValues) {
    const addPromise = mutation.mutateAsync(payload);

    await toast.promise(addPromise, {
      success: { title: t('addressBook.create.success') },
      error: { title: t('addressBook.create.error') },
      loading: { title: t('addressBook.create.loading') },
    });

    navigate('..');
  }

  return (
    <FormControl.Root>
      <FormProvider {...form}>
        <PageWrapper variant="center" gap="lg">
          <FormControl.Form onSubmit={form.handleSubmit(onSubmit)} style={{ display: 'none' }} />

          <PageTitle.Wrapper>
            <Breadcrumbs current={t('common.addNew')} />

            <FormControl.SubmitButton asChild>
              <LoadingButton loading={mutation.isPending} type="submit">
                {t('common.create')}
              </LoadingButton>
            </FormControl.SubmitButton>
          </PageTitle.Wrapper>

          <MutationBanner mutation={mutation} />
          <AddressBookForm />
        </PageWrapper>
      </FormProvider>
    </FormControl.Root>
  );
}
